import { omit, orderBy, pick } from 'lodash-es';
import { FeatureCollection } from 'geojson';

import type { Stop } from '../../schema/row-types.ts';
import type {
  DynamicQuery,
  RowOrderBy,
  RowQuery,
  StopQueryOptions,
} from '../../types/query.ts';
import { openDb } from '../sqlite-db.ts';
import { selectRows } from '../sqlite-query.ts';
import {
  formatBoundingBoxCondition,
  formatWhereConditions,
  type SqlClause,
} from '../sql-clauses.ts';
import { stopsToGeoJSONFeatureCollection } from '../geojson-utils.ts';
import { getAgencies } from './agencies.ts';
import { getStopAttributes } from '../gtfs-plus/stop-attributes.ts';
import { stopIdsForTrips } from './subqueries.ts';

type StopQuery = RowQuery<
  Stop & {
    route_id: string | null;
    trip_id: string | null;
    service_id: string | null;
    direction_id: number | null;
    shape_id: string | null;
  }
>;

/**
 * Returns an array of stops that match the query parameters. A `route_id`
 * query parameter may be passed to find all stops for a route. A `trip_id`
 * query parameter may be passed to find all stops for a trip. A
 * `direction_id` query parameter may be passed to find all stops for a
 * direction.
 * @param query Column values to match, as single values or arrays
 * @param fields Columns to select, or every column when empty
 * @param orderBy Column and direction pairs to sort by
 * @param options Query options, including the database to read from and
 *                `bounding_box_side_m` to search a box centred on
 *                `stop_lat`/`stop_lon`
 * @returns Matching rows, containing only `fields` when it is not empty.
 *          Sorted nearest-first when `bounding_box_side_m` is set and
 *          `orderBy` is empty
 */
export function getStops<Fields extends keyof Stop>(
  query: StopQuery = {},
  fields: readonly Fields[] = [],
  orderBy: RowOrderBy<Stop> = [],
  options: StopQueryOptions = {},
) {
  const tableName = 'stops';
  const tripKeys = [
    'route_id',
    'trip_id',
    'service_id',
    'direction_id',
    'shape_id',
  ];
  const stopQueryOmitKeys = [...tripKeys];

  // If bounding_box_side_m is defined, search for stops inside a bounding box so omit `stop_lat` and `stop_lon`.
  if (options.bounding_box_side_m !== undefined) {
    stopQueryOmitKeys.push('stop_lat', 'stop_lon');
  }

  const where = formatWhereConditions(
    omit(query, stopQueryOmitKeys) as DynamicQuery,
    tableName,
  );
  const tripQuery = pick(query, tripKeys) as DynamicQuery;

  let orderByOverride: SqlClause | undefined;

  if (
    options.bounding_box_side_m !== undefined &&
    query.stop_lat !== undefined &&
    query.stop_lon !== undefined
  ) {
    where.push(
      formatBoundingBoxCondition(
        query.stop_lat as number | string,
        query.stop_lon as number | string,
        options.bounding_box_side_m,
      ),
    );

    // Add distance-based sorting if bounding_box_side_m is set and no other orderBy is set
    if (orderBy.length === 0) {
      const lat = Number(query.stop_lat);
      const lon = Number(query.stop_lon);
      orderByOverride = {
        clause:
          'ORDER BY (((stop_lat - ?) * (stop_lat - ?)) + ((stop_lon - ?) * (stop_lon - ?))) ASC',
        params: [lat, lat, lon, lon],
      };
    }
  }

  if (Object.values(tripQuery).length > 0) {
    const stopIds = stopIdsForTrips(tripQuery);
    where.push({
      clause: `stop_id IN (${stopIds.clause})`,
      params: stopIds.params,
    });
  }

  return selectRows<Stop, Fields>(
    tableName,
    { fields, where, orderBy, orderByOverride },
    options,
  );
}

/**
 * Returns geoJSON with stops. A `route_id` query parameter may be passed to
 * find all stops for a route. A `trip_id` query parameter may be passed to
 * find all stops for a trip. A `direction_id` query parameter may be passed
 * to find all stops for a direction.
 * @param query Column values to match, as single values or arrays
 * @param options Query options, including the database to read from
 * @returns A GeoJSON FeatureCollection of stop points, each carrying its stop
 *          attributes, serving routes, and agency name. Stops belonging to no
 *          route are omitted
 */
export function getStopsAsGeoJSON(
  query: StopQuery = {},
  options: StopQueryOptions = {},
): FeatureCollection {
  const db = options.db ?? openDb();
  const stops = getStops(query, [], [], options);

  // Get all agencies for reference
  const agencies = getAgencies({}, [], [], options);

  const preparedStops = stops.map((stop) => {
    const routeSubquery =
      'SELECT DISTINCT route_id FROM trips WHERE trip_id IN (SELECT DISTINCT trip_id FROM stop_times WHERE stop_id = ?)';
    const routes = db
      .prepare(`SELECT * FROM routes WHERE route_id IN (${routeSubquery})`)
      .all(stop.stop_id);

    const stopAttributes = getStopAttributes(
      { stop_id: stop.stop_id },
      [],
      [],
      options,
    );

    return {
      ...stop,
      ...(stopAttributes?.[0] || []),
      routes: orderBy(routes, (route: { route_short_name?: string }) =>
        route?.route_short_name
          ? Number.parseInt(route.route_short_name, 10)
          : 0,
      ),
      agency_name: agencies[0]?.agency_name ?? null,
    };
  });

  // Exclude stops not part of any route
  const filteredStops = preparedStops.filter((stop) => stop.routes.length > 0);

  return stopsToGeoJSONFeatureCollection(filteredStops);
}
